import { isConvertedLegacyStats } from "@/app/report/common/legacyStatsSection"
import { SECTION_TYPE } from "@/app/report/common/constants"
import type { StatsSettings } from "@/app/report/common/type"
import type { ReportTemplate } from "@/types/report"

/** 템플릿 안의 Custom Table 섹션 하나 — content 인덱스와 설정 */
export type TemplateCustomTableSection = {
  /** template.content 에서의 위치. PDF 빌더 아이템이 어느 표를 따르는지 가리키는 키 */
  index: number
  settings: StatsSettings
}

/**
 * 리포트 템플릿에서 Custom Table 섹션들을 꺼낸다 (일감 #75 — PDF 빌더 Table 이 따르는 설정의 출처).
 * 리포트 화면(daily/page.tsx createReport)과 같이 폐지 예정 Stats 는 조회 시점에 이미 Custom Table 로 대치돼 있다(#99).
 */
export const getTemplateCustomTableSections = (
  template?: ReportTemplate | null,
): TemplateCustomTableSection[] =>
  (template?.content ?? []).flatMap((section, index) =>
    section?.section === SECTION_TYPE.CUSTOM_TABLE &&
    section.settings &&
    !Array.isArray(section.settings)
      ? [{ index, settings: section.settings as StatsSettings }]
      : [],
  )

/** 아이템이 가리키는 섹션(sectionIndex). 없거나 템플릿에서 사라졌으면 첫 Custom Table 로 떨어진다 */
export const resolveTemplateCustomTableSection = (
  sections: TemplateCustomTableSection[],
  sectionIndex?: number,
): TemplateCustomTableSection | null =>
  sections.find((section) => section.index === sectionIndex) ?? sections[0] ?? null

/**
 * PDF 빌더 Table 이 따를 섹션 — 옛 Stats(Stats by Player)에서 변환된 표는 빼고 진짜 Custom Table 중에서 고른다
 * (2026-09-30 요청). 진짜 Custom Table 이 없으면 변환된 표라도 쓴다. sectionIndex 가 후보 밖이면 첫 후보.
 */
/**
 * 옛 Stats 표인지 — 읽을 때 변환된 것(메모리 표식)이거나, 설정 화면에서 저장돼 customTable 로 굳은 것(제목이
 * "Stats by Player"/"Stats by Activity" 그대로). 저장된 것은 제목 말고는 구분할 표식이 없다
 */
export const isLegacyStatsTable = (settings: StatsSettings): boolean =>
  isConvertedLegacyStats(settings) ||
  /^\s*stats\s*by\s*(player|activity)\s*$/i.test(settings.title ?? "") ||
  /^stats(ByPlayer|ByActivity)?$/i.test((settings.title ?? "").trim())

export const getBuilderTableSections = (
  template?: ReportTemplate | null,
): TemplateCustomTableSection[] => {
  const all = getTemplateCustomTableSections(template)
  const custom = all.filter((section) => !isLegacyStatsTable(section.settings))

  return custom.length ? custom : all
}

export const resolveBuilderTableSection = (
  sections: TemplateCustomTableSection[],
  sectionIndex?: number,
): TemplateCustomTableSection | null => resolveTemplateCustomTableSection(sections, sectionIndex)
